/*
 * Shared pieces for the transaction pages:
 *   CXOPanel/transactionEdutech, CXOPanel/transactionEdutechStudyMaterial,
 *   CXOPanel/transactionDetails, Finance/transactionPublication.
 * Layout uses Custom* components + Bootstrap utilities only — no page CSS.
 */
import React, { useRef, useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { Receipt, User } from "lucide-react";
import { getBatchName, getParentCourseNew } from "../../Action/courseAction";
import { getSubExam } from "../../Action/adminPanelAction";
import {
  CustomButton,
  CustomDropdowns,
  CustomIconButton,
  CustomModal,
  WhatsAppLogo,
} from "../../Component/customComponents";

/* ---------- constants & formatters ---------- */

export const STATUS_OPTIONS = [
  { _id: 2, name: "Successful" },
  { _id: 0, name: "Pending" },
  { _id: 9, name: "Cancelled" },
  { _id: 10, name: "Cancelled Done" }, // UI-only: sent to the API as 9
];

// Publication transactions have no "Cancelled Done"
export const PUBLICATION_STATUS_OPTIONS = STATUS_OPTIONS.filter((s) => s._id !== 10);

export const LOCKED_PAYMENT_ROLES = ["SALESDIRECTOR", "VPOPERATIONS"]; // always paymentType 3

/** Some APIs send userDetails as an array — always give the screen one object */
export const firstUser = (userDetails) =>
  Array.isArray(userDetails) ? userDetails[0] || null : userDetails || null;

/** Publication-type totals: publication = Σ taxFreeAmount, shopify = Σ shopifyAmount */
export const computePublicationTotals = (list) =>
  (list || []).reduce(
    (acc, item) => {
      (item?.courses || []).forEach((course) => {
        acc.shopify += Number(course?.shopifyAmount) || 0;
        acc.publication += Number(course?.taxFreeAmount) || 0;
      });
      return acc;
    },
    { shopify: 0, publication: 0 },
  );

export const todayISO = () => moment.utc(new Date()).format("YYYY-MM-DD");

/** Mobile number with the middle hidden: 7812345678 → 78xxxxxx78. Missing → "—"; 4 chars or fewer shown as is */
export const maskMobile = (value) => {
  const text = value === null || value === undefined ? "" : String(value).trim();
  if (!text) return "—";
  if (text.length <= 4) return text;
  return `${text.slice(0, 2)}${"x".repeat(text.length - 4)}${text.slice(-2)}`;
};

export const formatINR = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

export const formatDateRange = (from, to) =>
  `${moment(from).format("DD/MM/YYYY")} – ${moment(to).format("DD/MM/YYYY")}`;

/** Cancelled (9) = courses NOT yet cancelled; Cancelled Done (10) = courses cancelled */
export const filterByCancelStatus = (list, status) => {
  if (status !== 9 && status !== 10) return list || [];
  const keep =
    status === 9
      ? (c) => c?.cancelOrderStatus === undefined
      : (c) => c?.cancelOrderStatus === true;
  return (list || [])
    .map((item) => ({ ...item, courses: (item?.courses || []).filter(keep) }))
    .filter((item) => item.courses.length > 0);
};

/** Filters every transaction page sends the same way */
export const buildBasePostData = (f, status, isPaymentLocked) => {
  const postData = {
    startDate: f.startDate,
    endDate: f.endDate,
    status: status === 10 ? 9 : status,
  };
  if (f.stateName) postData.stateId = f.stateName._id;
  if (f.exam) postData.examTypeId = f.exam._id;
  if (f.subExam) postData.subExamTypeId = f.subExam._id;
  if (f.examStream) postData.baseCourseId = f.examStream._id;
  if (f.batchName) postData.batchId = f.batchName._id;
  if (f.couponName?.trim()) postData.couponName = f.couponName.trim();
  if (isPaymentLocked) postData.paymentType = 3;
  else if (f.selectedPayment) postData.paymentType = f.selectedPayment._id;
  return postData;
};

export const openWhatsAppChat = (mobileNo) => {
  const wabaNumber = "9172266888";
  // only a real 10-digit number goes into the URL ("+91 98765-43210" → 9876543210)
  const digits = String(mobileNo ?? "").replace(/\D/g, "");
  const local = digits.length > 10 ? digits.slice(-10) : digits;
  if (local.length !== 10) return;
  // noopener: the third-party tab can't reach back into the admin panel (reverse tab-nabbing)
  window.open(`https://web.doubletick.io/conversations/91${wabaNumber}/91${local}`, "_blank", "noopener,noreferrer");
};

/* ---------- small display pieces ---------- */

/**
 * One value per item (course, platform…), stacked inside a cell.
 *   lineHeight  give every line the same height, so several columns that
 *               list the same items line up row-by-row (e.g. 30)
 *   align       "left" (default) | "right" | "center" for fixed-height lines
 */
export const CourseLines = ({ courses, pick, lineHeight, align = "left" }) => {
  const lines = (courses || []).map(pick).filter((v) => v !== null && v !== undefined && v !== "");
  if (!lines.length) return "—";
  const lineStyle = lineHeight
    ? {
        minHeight: lineHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start",
      }
    : undefined;
  return (
    <div className="d-flex flex-column gap-1">
      {lines.map((line, i) => (
        <span key={i} style={lineStyle}>
          {line}
        </span>
      ))}
    </div>
  );
};

/**
 * Names out of a string / array of strings / course objects ({ name } or { courseData: { name } }),
 * blanks dropped. `unique` removes repeats. Shared by BatchNames, search and Excel export.
 */
export const nameList = (names, { unique = true } = {}) => {
  const list = (Array.isArray(names) ? names : [names])
    .map((n) => (typeof n === "string" ? n : n?.name || n?.courseData?.name)) // `||`: { name: "" } falls back
    .map((n) => (n == null ? "" : String(n).trim()))
    .filter(Boolean);
  return unique ? [...new Set(list)] : list;
};
/** "A, B" for search / export ("" when there are none) */
export const namesText = (names) => nameList(names).join(", ");

/**
 * Batch / course / exam stream (parent course) name cell: the full name on one line, never cut with "…" — a long name scrolls
 * sideways inside the cell (thin scroll bar, see .tc-batchNameScroll in App.css) and hover shows it.
 * Several batches → a bullet list; more than `maxVisible` also scrolls up/down.
 *   names   a string, or an array of strings / course objects ({ name } or { courseData: { name } })
 *   scroll  false when other columns list the same courses line-by-line and must stay aligned
 *           (then repeated names are kept too, so line N is still course N)
 *   bold    keeps the bold look some pages used for the name
 *   fill    a single name uses the column's full width (no 292px cap) and still scrolls sideways when longer —
 *           give that column a width (e.g. "45%") so it takes the free space in tables with few columns
 */
export const BatchNames = ({ names, maxVisible = 3, scroll = true, bold = false, fill = false }) => {
  const list = nameList(names, { unique: scroll });
  if (!list.length) return "—";
  const fontWeight = bold ? 600 : undefined;
  if (list.length === 1) {
    return (
      <div className={`tc-batchNameScroll${fill ? " tc-batchNameFill" : ""}`} title={list[0]} style={{ fontWeight }}>
        {list[0]}
      </div>
    );
  }
  const scrolls = scroll && list.length > maxVisible;
  return (
    <div
      className="tc-batchNameScroll"
      title={list.join("\n")}
      style={{ fontWeight, ...(scrolls && { maxHeight: `${maxVisible * 1.5 + 0.6}em` }) }}
    >
      <ul className="mb-0 ps-3">
        {list.map((n, i) => (
          <li key={`${n}-${i}`} title={n} style={{ lineHeight: 1.5 }}>
            {n}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Table title with the date range underneath (no range line until both dates are set) */
export const TableTitle = ({ title, from, to }) => (
  <span className="d-flex flex-column">
    {title}
    {from && to && (
      <small className="text-muted fw-normal" style={{ fontSize: 12 }}>
        {formatDateRange(from, to)}
      </small>
    )}
  </span>
);

/** Label / value rows; the last row is bold (the total) */
export const DetailList = ({ rows }) => (
  <div>
    {rows.map(({ label, value }, i) => {
      const isLast = i === rows.length - 1;
      return (
        <div
          key={label}
          className={`d-flex justify-content-between gap-3 py-2 ${isLast ? "fw-bold fs-6" : "border-bottom"}`}
        >
          <span className={isLast ? "" : "text-muted"}>{label}</span>
          <span className="text-end" style={{ fontVariantNumeric: "tabular-nums" }}>
            {value}
          </span>
        </div>
      );
    })}
  </div>
);

/** Payment Status dropdown for the table toolbar */
export const StatusDropdown = ({ isTechnical, value, onChange, options = STATUS_OPTIONS }) => (
  <div style={{ minWidth: 190 }}>
    <CustomDropdowns
      placeholder="Payment Status"
      options={isTechnical ? options.filter((s) => s._id !== 2) : options}
      value={options.find((s) => s._id === value) || ""}
      onChange={(option) => option && onChange(option._id)}
      search={false}
      clearable={false}
    />
  </div>
);

/** "Total" breakdown popup */
export const TotalsModal = ({ open, onClose, subtitle, rows }) => (
  <CustomModal
    open={open}
    onClose={onClose}
    title="Total Breakdown"
    subtitle={subtitle}
    icon={<Receipt size={18} />}
    size="sm"
    footer={
      <CustomButton variant="outline" onClick={onClose}>
        Close
      </CustomButton>
    }
  >
    <DetailList rows={rows} />
  </CustomModal>
);

/** Row "View" popup with student details (+ WhatsApp for CXO) */
export const UserDetailsModal = ({ row, onClose, canChat }) => (
  <CustomModal
    open={Boolean(row)}
    onClose={onClose}
    title="User Details"
    subtitle={row?.orderNo ? `Order ${row.orderNo}` : undefined}
    icon={<User size={18} />}
    size="sm"
    footer={
      <>
        {canChat && firstUser(row?.userDetails)?.mobileNo && (
          <CustomIconButton
            variant="full"
            icon={<WhatsAppLogo />}
            tone="whatsapp"
            onClick={() => openWhatsAppChat(firstUser(row.userDetails).mobileNo)}
          >
            Chat on WhatsApp
          </CustomIconButton>
        )}
        <CustomButton variant="outline" onClick={onClose}>
          Close
        </CustomButton>
      </>
    }
  >
    <DetailList
      rows={[
        {
          label: "Student Name",
          value:
            [firstUser(row?.userDetails)?.firstName, firstUser(row?.userDetails)?.lastName].filter(Boolean).join(" ") ||
            "—",
        },
        { label: "Mobile Number", value: firstUser(row?.userDetails)?.mobileNo || "—" },
      ]}
    />
  </CustomModal>
);

/** WhatsApp button for a table row */
export const RowWhatsAppButton = ({ row }) => (
  <CustomIconButton
    icon={<WhatsAppLogo />}
    tone="whatsapp"
    size="sm"
    title="Chat on WhatsApp"
    onClick={() => openWhatsAppChat(firstUser(row?.userDetails)?.mobileNo)}
    disabled={!firstUser(row?.userDetails)?.mobileNo}
  />
);

/* ---------- Exam → Sub Exam → Stream → Batch option lists ---------- */

/**
 * Loads the dependent dropdown lists for the filter drawer.
 *   batchFilter(item)  optional: keep only some batches
 *   useSection         pass the selected section's type to the batch API
 *   withBatches        false = page has no Batch filter, never call the batch API
 * Returns the lists plus `onFilterChange` / `onDrawerOpen` for CustomAdminTable.
 */
export const useCascadingLists = ({ batchFilter, useSection = false, withBatches = true } = {}) => {
  const dispatch = useDispatch();
  const [subExamList, setSubExamList] = useState([]);
  const [examStreamList, setExamStreamList] = useState([]);
  const [batchList, setBatchList] = useState([]);
  const loadedForRef = useRef("");

  const keyOf = (v) =>
    [v.exam?._id, v.subExam?._id, v.examStream?._id, useSection ? v.sectionName?.type : ""].join("|");

  const loadBatches = (exam, stream, section) => {
    setBatchList([]);
    if (!withBatches || !exam || !stream) return;
    const payload = { baseCourseId: stream._id, examTypeId: exam._id };
    if (useSection && section) payload.sectionTypes = [section.type];
    dispatch(
      getBatchName(
        payload,
        (res) => {
          const list = res?.data || [];
          setBatchList(batchFilter ? list.filter(batchFilter) : list);
        },
        () => {},
      ),
    );
  };

  const loadStreams = (exam, subExam) => {
    setExamStreamList([]);
    if (!exam) return;
    const path = subExam ? `${exam._id}/${subExam._id}` : `${exam._id}`;
    dispatch(getParentCourseNew(path, (list) => setExamStreamList(list || [])));
  };

  const loadSubExams = (exam, { thenStreams = true } = {}) => {
    setSubExamList([]);
    if (!exam) return;
    dispatch(
      getSubExam({ examTypeId: exam._id }, (res) => {
        if (res?.data?.length > 0) setSubExamList(res.data);
        else if (thenStreams) loadStreams(exam);
      }),
    );
  };

  // CustomAdminTable → onFilterChange
  const onFilterChange = (key, value, draft) => {
    if (key === "exam") {
      setExamStreamList([]);
      setBatchList([]);
      loadSubExams(value);
    } else if (key === "subExam") {
      setBatchList([]);
      loadStreams(draft.exam, value);
    } else if (key === "examStream" || (useSection && key === "sectionName")) {
      loadBatches(draft.exam, draft.examStream, draft.sectionName);
    }
    loadedForRef.current = keyOf(draft);
  };

  // CustomAdminTable → onFilterDrawerOpen: lists back in sync after a Cancel
  const onDrawerOpen = (applied) => {
    if (loadedForRef.current === keyOf(applied)) return;
    loadedForRef.current = keyOf(applied);
    if (!applied.exam) {
      setSubExamList([]);
      setExamStreamList([]);
      setBatchList([]);
      return;
    }
    loadSubExams(applied.exam, { thenStreams: !applied.subExam });
    if (applied.subExam) loadStreams(applied.exam, applied.subExam);
    loadBatches(applied.exam, applied.examStream, applied.sectionName);
  };

  return { subExamList, examStreamList, batchList, onFilterChange, onDrawerOpen };
};

/**
 * Filter-drawer fields shared by the transaction pages (insert page-specific ones around them).
 * withBatch: false drops the Batch Name field.
 */
export const buildExamFilterFields = ({
  examList,
  subExamList,
  examStreamList,
  batchList,
  withBatch = true,
}) =>
  [
  {
    key: "exam",
    label: "Exam Name",
    type: "dropdown",
    options: examList,
    clears: ["subExam", "examStream", "batchName"],
  },
  {
    key: "subExam",
    label: "Sub Exam Name",
    type: "dropdown",
    options: subExamList,
    hidden: (v) => !v.exam || subExamList.length === 0,
    clears: ["examStream", "batchName"],
  },
  {
    key: "examStream",
    label: "Exam Stream",
    type: "dropdown",
    options: examStreamList,
    disabled: (v) => !v.exam,
    hint: (v) => (!v.exam ? "Select an exam first" : undefined),
    clears: ["batchName"],
  },
  withBatch && {
    key: "batchName",
    label: "Batch Name",
    type: "dropdown",
    options: batchList,
    disabled: (v) => !v.examStream,
    hint: (v) => (!v.examStream ? "Select an exam stream first" : undefined),
  },
  ].filter(Boolean);

export const buildDateFilterFields = () => [
  {
    key: "startDate",
    label: "Start Date",
    type: "date",
    half: true,
    isRequired: true,
    defaultValue: todayISO,
    maxDate: () => todayISO(),
  },
  {
    key: "endDate",
    label: "End Date",
    type: "date",
    half: true,
    isRequired: true,
    defaultValue: todayISO,
    minDate: (v) => v.startDate,
    maxDate: () => todayISO(),
    validate: (value, v) =>
      value && v.startDate && value < v.startDate ? "End date can't be before the start date" : "",
  },
];
